import { connect, type ConnectedProps } from 'react-redux';
import * as yup from 'yup';
import { type SubmitHandler } from 'react-hook-form';
import { useRef } from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import { isAppLoading } from '@/core/features/loader';
import SelectField from '@/components/SelectField';
import Form, { type FormElement } from '@/components/Form';
import { updateUserRole } from '@/slices/user';
import { roleOptions } from '@/slices/members';
import { type RootState } from '@/store';
import { normalizeSimpleFormOptions, useYupValidationResolver } from '@/utils';
import { openModal, type ModalContentProps } from '../../../state';
import { ModalLayout } from '../../layouts';
import styles from './styles';

const validationSchema = yup.object({
  role_id: yup.string().required('Role is required'),
});

type FormValues = {
  role_id?: string;
};

type Props = ModalContentProps & ConnectedProps<typeof connector>;

const ViewMemberModal = ({
  closeModal,
  modalData: { member, propertyId },
  roleOptions,
  isAppLoading,
  updateUserRole,
  openModal,
}: Props) => {
  const formRef = useRef<FormElement>(null);
  const resolver = useYupValidationResolver(validationSchema);

  const handleOpenFileUpload = () => {
    openModal({
      modalType: 'fileUpload',
      modalData: { member_id: member.user_id, propertyId },
    });
  };

  const defaultValues: FormValues = {
    role_id: String(roleOptions.find((role) => role.name === member.role)?.id),
  };

  const handleAccept = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const onSubmit: SubmitHandler<FormValues> = ({ role_id }) => {
    updateUserRole({
      propertyId,
      user_id: member.user_id,
      role_id: Number(role_id),
    });
  };

  return (
    <ModalLayout
      closeModal={closeModal}
      title="Update Member"
      onAccept={handleAccept}
      acceptText={isAppLoading ? 'Saving...' : 'Save'}
      acceptDisabled={isAppLoading}
      onCancel={closeModal}
      cancelText="Cancel"
    >
      <Form
        ref={formRef}
        sx={styles.form}
        resolver={resolver}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
      >
        <Box sx={styles.header}>
          <Avatar
            src={member?.avatar}
            alt={member?.full_name}
            sx={styles.avatar}
            onClick={handleOpenFileUpload}
          />

          <Typography sx={styles.email} variant="body2">
            {member?.email}
          </Typography>
        </Box>

        <SelectField
          name="role_id"
          label="Role"
          options={normalizeSimpleFormOptions(roleOptions)}
        />
      </Form>
    </ModalLayout>
  );
};

const connector = connect(
  (state: RootState) => ({
    roleOptions: roleOptions(state),
    isAppLoading: isAppLoading(state),
  }),
  { updateUserRole, openModal },
);

export default connector(ViewMemberModal);
